import { alpha, ThemeOptions } from '../material';

// MUI's `shadows` is a 25-entry tuple; pad the tail with the last design entry.
const coupaShadows = [
  'none',
  '0px 0px 0px 1px rgba(26, 31, 39, 0.08) inset',
  '0px 2px 3px 0px rgba(0, 0, 0, 0.2)',
  '0px 2px 8px 0px rgba(0, 0, 0, 0.2)',
  '0px 12px 16px 0px rgba(0, 0, 0, 0.1)',
  '0px 20px 40px 0px rgba(0, 0, 0, 0.1)',
  '0px 6px 10px 0px rgba(0,0,0,0.1), 0px 1px 18px 0px rgba(0,0,0,0.06)',
] as const;

const coupaShadowsFull = [
  ...coupaShadows,
  ...Array(18).fill(coupaShadows[6]),
] as ThemeOptions['shadows'];

export const coupaThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: '#0059d6',
      light: '#3996f9',
      dark: '#022f88',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#607590',
      light: '#96aac0',
      dark: '#586b84',
      contrastText: '#ffffff',
    },
    success: {
      main: '#008571',
      light: '#00a385',
      dark: '#00705f',
      contrastText: '#ffffff',
    },
    info: {
      main: '#0971f1',
      light: '#3996f9',
      dark: '#0342b0',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#e08500',
      light: '#ff9900',
      dark: '#c27300',
      contrastText: '#ffffff',
    },
    error: {
      main: '#e70848',
      light: '#f63761',
      dark: '#ca073e',
      contrastText: '#ffffff',
    },
    aurora: {
      main: '#8811E3',
      light: '#8811E3',
      dark: '#720EBE',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#1b1f28',
      secondary: '#455468',
      disabled: '#8298af',
    },
    action: {
      active: 'rgba(0, 0, 0, 0.54)',
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(0, 0, 0, 0.08)',
      focus: 'rgba(0, 0, 0, 0.12)',
      disabled: '#8298AF',
      disabledBackground: '#F5F7FA',
      activatedOpacity: 0.12,
      disabledOpacity: 0.38,
      focusOpacity: 0.12,
      hoverOpacity: 0.04,
      selectedOpacity: 0.08,
    },
    grey: {
      50: '#ffffff',
      100: '#f5f7fa',
      200: '#eef1f6',
      300: '#c6d1dd',
      400: '#a9b8c9',
      500: '#8298af',
      600: '#607590',
      700: '#455468',
      800: '#2b3442',
      900: '#1b1f28',
      A100: '#f5f7fa',
      A200: '#eef1f6',
      A400: '#a9b8c9',
      A700: '#455468',
    },
    divider: '#0000001f',
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    other: {
      muted: '#455468',
      // Coupa tooltips are light: white surface with a subtle border
      // (see `MuiTooltip` override below).
      tooltip: '#ffffff',
      defaultPrimary: '#0059d6',
      outlinedBorder: '#c6d1dd',
      backdropOverlay: 'rgba(27, 31, 40, 0.70)',
      filledInputBackground: '#f5f7fa',
      standardInputLine: 'rgba(26, 31, 39, 0.42)',
    },
    elevation: {
      0: {
        background: 'none',
        shadow: '0px 0px 0px 1px rgba(26, 31, 39, 0.08) inset',
      },
      2: {
        background: '#ffffff',
        shadow: '0px 0px 0px 1px rgba(26, 31, 39, 0.08) inset',
      },
      4: {
        background: '#ffffff',
        shadow:
          '0px 1px 4px 1px rgba(13, 17, 23, 0.06), 0px 2px 8px 0px rgba(13, 17, 23, 0.06)',
      },
      6: {
        background: '#ffffff',
        shadow:
          '0px 2px 4px 1px rgba(13, 17, 23, 0.1), 0px 2px 8px 0px rgba(13, 17, 23, 0.06)',
      },
      8: {
        background: '#ffffff',
        shadow:
          '0px 3px 4px 1px rgba(13, 17, 23, 0.12), 0px 4px 8px 0px rgba(13, 17, 23, 0.08)',
      },
      12: {
        background: '#ffffff',
        shadow:
          '0px 2px 8px 1px rgba(13, 17, 23, 0.14), 0px 4px 8px 0px rgba(13, 17, 23, 0.08)',
      },
      24: {
        background: '#ffffff',
        shadow:
          '0px 4px 6px 1px rgba(13, 17, 23, 0.18), 0px 4px 10px 0px rgba(13, 17, 23, 0.06)',
      },
    },
    // @ts-expect-error There must be a way to fix this
    DataGrid: {
      bg: '#ffffff',
    },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: coupaShadowsFull,
  typography: {
    fontFamily:
      '"Nunito Sans Variable", "Nunito Sans", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    htmlFontSize: 10,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
    h1: {
      fontSize: '9.6rem',
      fontWeight: 300,
      lineHeight: 1.167,
    },
    h2: {
      fontSize: '6rem',
      fontWeight: 300,
      lineHeight: 1.2,
    },
    h3: {
      fontSize: '2.4rem',
      fontWeight: 700,
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    h4: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.6,
      letterSpacing: 0,
    },
    h5: {
      fontSize: '2rem',
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: 0,
    },
    h6: {
      fontSize: '1.6rem',
      fontWeight: 700,
      lineHeight: 1.25,
      letterSpacing: 0,
    },
    subtitle1: {
      fontSize: '1.6rem',
      fontWeight: 400,
      lineHeight: 1.75,
    },
    subtitle2: {
      fontSize: '1.4rem',
      fontWeight: 600,
      lineHeight: 1.429,
      letterSpacing: 0,
    },
    body1: {
      fontSize: '1.6rem',
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    body2: {
      fontSize: '1.4rem',
      fontWeight: 400,
      lineHeight: 1.429,
      letterSpacing: 0,
    },
    button: {
      fontSize: '1.4rem',
      fontWeight: 600,
      lineHeight: 1.429,
      letterSpacing: 0,
      textTransform: 'none',
    },
    caption: {
      fontSize: '1.2rem',
      fontWeight: 400,
      lineHeight: 1.334,
      letterSpacing: 0,
    },
    overline: {
      fontSize: '1.2rem',
      fontWeight: 400,
      lineHeight: 2.66,
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'transparent',
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 48,
        },
        indicator: ({ theme }) => ({
          height: 2,
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          minHeight: 48,
          fontSize: '1.4rem',
          fontWeight: 400,
          letterSpacing: 0,
          textTransform: 'none',
          color: theme.palette.text.secondary,
          '&:hover': {
            color: theme.palette.text.primary,
          },
          '&.Mui-selected': {
            fontWeight: 400,
            color: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 'none',
          paddingTop: 8,
          paddingBottom: 8,
          '&.Mui-selected': {
            backgroundColor: theme.palette.grey[100],
            boxShadow: `inset 3px 0 0 0 ${theme.palette.primary.main}`,
            '&:hover': {
              backgroundColor: theme.palette.grey[200],
            },
          },
        }),
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          borderBottom: 'none',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: ({ theme }) => ({
          fontSize: '1.4rem',
          fontWeight: 400,
          color: theme.palette.text.primary,
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderColor: theme.palette.divider,
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          lineHeight: 1.3,
          color: theme.palette.text.primary,
          border: '1px solid',
          borderRadius: 100,
          height: 24,
          fontSize: '1.2rem',
          fontWeight: 400,
        }),
        label: {
          paddingLeft: 8,
          paddingRight: 8,
        },
        icon: {
          fontSize: '1.6rem',
          width: '1.6rem',
          height: '1.6rem',
          marginLeft: 6,
          marginRight: -2,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: ({ theme }) => ({
          color: theme.palette.text.primary,
          textAlign: 'center',
          boxShadow:
            '0px 4px 6px 1px rgba(13, 17, 23, 0.18), 0px 4px 10px 0px rgba(13, 17, 23, 0.06)',
          backgroundColor: theme.palette.other.tooltip,
          border: `1px solid ${theme.palette.grey[300]}`,
          fontSize: '1.2rem',
          fontWeight: 400,
          lineHeight: 1.334,
          padding: '8px 12px',
          borderRadius: 8,
        }),
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        head: ({ theme }) => ({
          fontWeight: 400,
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: ({ theme }) => ({
          border: 'none',
          backgroundColor: theme.palette.background.paper,
          '--DataGrid-rowBorderColor': theme.palette.divider,
        }),
        columnHeaders: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }),
        columnHeader: {
          '&:focus, &:focus-within': {
            outline: 'none',
          },
        },
        columnHeaderTitle: ({ theme }) => ({
          fontWeight: 600,
          color: theme.palette.text.secondary,
        }),
        columnSeparator: {
          opacity: 0,
          '&.MuiDataGrid-columnSeparator--resizable:hover, &.MuiDataGrid-columnSeparator--resizing':
            {
              opacity: 1,
            },
        },
        cell: ({ theme }) => ({
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
          '&:focus, &:focus-within': {
            outline: 'none',
          },
        }),
        row: ({ theme }) => ({
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
          },
        }),
        withBorderColor: ({ theme }) => ({
          borderColor: theme.palette.divider,
        }),
        footerContainer: ({ theme }) => ({
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
        }),
      },
    },
  },
};
